import { useState } from 'react';

import { IconX } from '../incidents/icons';

interface ChipInputProps {
  id: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Valeurs proposées en autocomplétion (`<datalist>`). */
  suggestions?: string[];
  'aria-label'?: string;
}

/**
 * Saisie libre de valeurs sous forme de chips. Entrée, virgule ou perte de
 * focus ajoute la valeur courante ; Retour arrière sur un champ vide retire la
 * dernière chip. Sert aux filtres de périmètre du header et à l'éditeur d'accès.
 */
export const ChipInput = ({
  id,
  values,
  onChange,
  placeholder,
  disabled,
  suggestions,
  'aria-label': ariaLabel,
}: ChipInputProps) => {
  const [draft, setDraft] = useState('');

  const commit = () => {
    const value = draft.trim();
    if (!value) return;
    if (!values.includes(value)) onChange([...values, value]);
    setDraft('');
  };

  const remove = (value: string) => onChange(values.filter(v => v !== value));

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  const available = suggestions?.filter(s => !values.includes(s)) ?? [];
  const listId = available.length > 0 ? `${id}-suggestions` : undefined;

  return (
    <div
      className={[
        'min-h-9 w-full px-2 py-1 flex flex-wrap items-center gap-1 bg-white border rounded-md transition-colors',
        'focus-within:border-zinc-400 focus-within:ring-2 focus-within:ring-zinc-900/10',
        disabled ? 'border-zinc-200 bg-zinc-50 cursor-not-allowed' : 'border-zinc-300',
      ].join(' ')}
    >
      {values.map(value => (
        <span
          key={value}
          className="inline-flex items-center gap-1 rounded-md bg-zinc-100 text-zinc-700 font-mono text-xs tabular-nums px-1.5 py-0.5"
        >
          {value}
          <button
            type="button"
            onClick={() => remove(value)}
            disabled={disabled}
            aria-label={`Retirer ${value}`}
            className="text-zinc-400 hover:text-zinc-900 disabled:cursor-not-allowed"
          >
            <IconX width={10} height={10} />
          </button>
        </span>
      ))}
      <input
        id={id}
        list={listId}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={values.length === 0 ? placeholder : undefined}
        disabled={disabled}
        aria-label={ariaLabel}
        autoComplete="off"
        className="flex-1 min-w-[96px] h-6 text-sm bg-transparent text-zinc-900 placeholder:text-zinc-400 focus:outline-none disabled:cursor-not-allowed"
      />
      {listId ? (
        <datalist id={listId}>
          {available.map(s => (
            <option key={s} value={s} />
          ))}
        </datalist>
      ) : null}
    </div>
  );
};
